import { forwardRef, type ReactNode, type SelectHTMLAttributes } from 'react';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  id: string;
  label: ReactNode;
  options: SelectOption[];
  placeholder?: string;
  helpText?: ReactNode;
  errorText?: ReactNode;
  /** Visually hide the label (kept in DOM for screen-readers). For inline
   *  contexts like per-row edit cells where a column header already labels
   *  the control. */
  hideLabel?: boolean;
  /** Always-visible bordered chrome for table inline-edit cells. */
  table?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { id, label, options, placeholder, helpText, errorText, className, hideLabel, table, ...rest },
  ref
) {
  const helpId = helpText ? `${id}-help` : undefined;
  const errorId = errorText ? `${id}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div
      className={['pn-field', table ? 'pn-field-table' : null, className]
        .filter(Boolean)
        .join(' ')}
    >
      <label
        htmlFor={id}
        className={[
          'pn-field-label',
          hideLabel ? 'pn-field-label-hidden' : null
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {label}
      </label>
      <div className="pn-field-select-wrap">
        <select
          ref={ref}
          id={id}
          className="pn-field-input pn-field-select"
          aria-describedby={describedBy}
          aria-invalid={errorText ? true : undefined}
          {...rest}
        >
          {placeholder ? <option value="">{placeholder}</option> : null}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      {helpText ? (
        <span id={helpId} className="pn-field-help">
          {helpText}
        </span>
      ) : null}
      {errorText ? (
        <span id={errorId} className="pn-field-error" role="alert">
          {errorText}
        </span>
      ) : null}
    </div>
  );
});
